/** Lock body text selection during drag/resize; always release on global pointer/window events. */

let lockCount = 0;
let listenersBound = false;

function releaseAll() {
  lockCount = 0;
  if (typeof document === "undefined") return;
  document.body.classList.remove("fw-selection-locked");
  document.body.style.userSelect = "";
  document.body.style.cursor = "";
}

function onGlobalRelease() {
  releaseAll();
}

function bindGlobalRelease() {
  if (listenersBound || typeof window === "undefined") return;
  listenersBound = true;
  window.addEventListener("mouseup", onGlobalRelease);
  window.addEventListener("pointerup", onGlobalRelease);
  window.addEventListener("pointercancel", onGlobalRelease);
  window.addEventListener("blur", onGlobalRelease);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") releaseAll();
  });
}

export function acquireSelectionLock(options = {}) {
  if (typeof document === "undefined") return;
  bindGlobalRelease();
  if (lockCount === 0) {
    document.body.classList.add("fw-selection-locked");
    document.body.style.userSelect = "none";
    if (options.cursor) document.body.style.cursor = options.cursor;
  }
  lockCount += 1;
}

export function releaseSelectionLock() {
  if (typeof document === "undefined") return;
  lockCount = Math.max(0, lockCount - 1);
  if (lockCount === 0) {
    document.body.classList.remove("fw-selection-locked");
    document.body.style.userSelect = "";
    document.body.style.cursor = "";
  }
}

export function forceReleaseSelectionLock() {
  releaseAll();
}

export function onCopyableRegionKeydown(e) {
  if (!(e.ctrlKey || e.metaKey) || String(e.key || "").toLowerCase() !== "a") return;
  const root = e.currentTarget;
  if (!root) return;
  e.preventDefault();
  e.stopPropagation();
  const sel = window.getSelection();
  if (!sel) return;
  const range = document.createRange();
  range.selectNodeContents(root);
  sel.removeAllRanges();
  sel.addRange(range);
}

function resolveCopyMountNode(container) {
  if (container && container.appendChild) return container;
  const active = document.activeElement;
  if (active && active.closest) {
    const modal = active.closest(".modal-content, .modal-dialog, .modal");
    if (modal) return modal;
  }
  return document.body;
}

function execCopyFromTextarea(ta, value) {
  if (!ta) return false;
  ta.value = value;
  ta.focus({ preventScroll: true });
  ta.select();
  ta.setSelectionRange(0, value.length);
  try {
    return document.execCommand("copy");
  } catch (_) {
    return false;
  }
}

/**
 * Copy text to clipboard. Tries Clipboard API first (user gesture), then execCommand
 * via a textarea inside the modal when possible.
 * @param {string} text
 * @param {{ container?: HTMLElement, textareaEl?: HTMLTextAreaElement }} [options]
 * @returns {Promise<boolean>}
 */
export async function copyTextToClipboard(text, options = {}) {
  const value = text == null ? "" : String(text);
  if (!value) return false;

  forceReleaseSelectionLock();

  if (execCopyFromTextarea(options.textareaEl, value)) {
    return true;
  }

  if (
    typeof navigator !== "undefined" &&
    navigator.clipboard &&
    typeof navigator.clipboard.writeText === "function"
  ) {
    try {
      await navigator.clipboard.writeText(value);
      return true;
    } catch (_) {
      /* fall through */
    }
  }

  const mount = resolveCopyMountNode(options.container);
  const ta = document.createElement("textarea");
  ta.setAttribute("readonly", "");
  ta.setAttribute("aria-hidden", "true");
  ta.style.position = "fixed";
  ta.style.top = "0";
  ta.style.left = "0";
  ta.style.width = "2em";
  ta.style.height = "2em";
  ta.style.padding = "0";
  ta.style.border = "none";
  ta.style.outline = "none";
  ta.style.boxShadow = "none";
  ta.style.background = "transparent";
  ta.style.opacity = "0";
  mount.appendChild(ta);
  const ok = execCopyFromTextarea(ta, value);
  if (ta.parentNode) ta.parentNode.removeChild(ta);
  return ok;
}
